import type { AppointmentDossier } from './types';

export type DossierEnrichment = {
  clinicalAlerts: AppointmentDossier['clinicalAlerts'];
  opportunityWalkinSummary: string[];
  opportunityWalkoutSummary: string[];
  family: AppointmentDossier['family'];
  comfort: AppointmentDossier['comfort'];
  patientValue: AppointmentDossier['patientValue'];
  notes: AppointmentDossier['notes'];
};

export const DOSSIER_ENRICHMENTS: Record<string, DossierEnrichment> = {
  a1: {
    clinicalAlerts: {
      allergies: ['Latex'],
      conditions: ['Hypertension', 'Mild sleep apnea'],
      medications: ['Amlodipine 5mg', 'Aspirin 81mg'],
      mindful: ['Prefers morning appointments', 'Ask about crown sensitivity at checkout'],
    },
    opportunityWalkinSummary: [
      'Crown seat visit — verify shade and contacts before cement. Patient is reliable and usually on time.',
      'Production $1,165 — confirm estimated portion collected before dismissal.',
    ],
    opportunityWalkoutSummary: [
      'Schedule 2-week follow-up if bite feels high after anesthesia wears off.',
      'Email receipt and crown care instructions before patient leaves.',
    ],
    family: [
      { name: 'Elena Blake', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Marco', rel: 'Son', status: 'Recall due Aug' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Americano' },
      { label: 'Music', value: 'Classic rock' },
      { label: 'Blanket', value: 'No' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$9,890' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '09 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Leonard (Clinical)',
        when: 'May 19, 3:40 PM',
        text: 'Shade verified with patient at prep. Lab case in hand for today\'s seat.',
      },
    ],
  },
  a2: {
    clinicalAlerts: {
      allergies: ['Nickel'],
      conditions: ['Seasonal allergies'],
      medications: ['Cetirizine 10mg PRN'],
      mindful: ['Birthday today — card at front desk', 'Interested in cosmetic consult'],
    },
    opportunityWalkinSummary: [
      'Birthday visit — team card signed. Composite on tooth #14, routine restorative.',
      'Mentioned wedding in September — whitening consult is a soft upsell if time allows.',
    ],
    opportunityWalkoutSummary: [
      'Offer whitening info sheet and book consult if she bites.',
      'Confirm 6-month recall before checkout.',
    ],
    family: [
      { name: 'James Harrell', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Sophie', rel: 'Sister', status: 'Referred 2 patients' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Latte' },
      { label: 'Music', value: 'Pop' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$6,240' },
      { label: 'Health score', value: '09 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Mary (Front desk)',
        when: 'Today, 8:55 AM',
        text: 'Birthday flagged — signed card on desk. Patient confirmed via text last night.',
      },
    ],
  },
  a3: {
    clinicalAlerts: {
      allergies: ['Shellfish'],
      conditions: ['GERD'],
      medications: ['Omeprazole 20mg'],
      mindful: ['Dislikes cold water', 'Balance on account — handle gently'],
    },
    opportunityWalkinSummary: [
      'Forms incomplete — send tablet link immediately at check-in before seating.',
      'Outstanding balance from October — review payment options at checkout, not in operatory.',
    ],
    opportunityWalkoutSummary: [
      'Collect $127 (balance + copay) before dismissal.',
      'Book next hygiene if perio findings noted today.',
    ],
    family: [
      { name: 'Diane Tucker', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Ryan', rel: 'Son', status: 'New patient pending' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'None' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$4,120' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '07 / 10' },
      { label: 'Balance', value: '$102', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 9:14 AM',
        text: 'Forms link sent — still not completed. Balance reminder added to checkout script.',
      },
    ],
  },
  a4: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Anxiety disorder'],
      medications: ['None daily'],
      mindful: ['Anxiety flag — offer nitrous proactively', 'First visit — allow extra time'],
    },
    opportunityWalkinSummary: [
      'First visit — set a warm, unhurried tone. Anxiety flag means Op-3 and nitrous offer before exam.',
      'Capture household insurance info — patient mentioned fiancée may switch to same plan.',
    ],
    opportunityWalkoutSummary: [
      'Book hygiene follow-up if perio noted during exam.',
      'Send welcome packet and new patient survey link within 24 hours.',
    ],
    family: [
      { name: 'Lily Chen', rel: 'Fiancée', status: 'May join plan Q3' },
      { name: 'Wei Zhao', rel: 'Father', status: 'Referred by colleague' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Declines' },
      { label: 'Music', value: 'Lo-fi' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'Nitrous (anxiety)' },
    ],
    patientValue: [
      { label: 'Total value', value: '$709' },
      { label: 'Health score', value: '06 / 10' },
      { label: 'Relationship', value: '05 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Dr. Carter',
        when: 'May 19, 4:00 PM',
        text: 'New patient intake reviewed — anxiety noted. Op-3 reserved, nitrous on tray list.',
      },
    ],
  },
  a5: {
    clinicalAlerts: {
      allergies: ['Codeine'],
      conditions: ['TMJ mild'],
      medications: ['Ibuprofen PRN'],
      mindful: ['Often 10 min late — do not rush', 'Unconfirmed — confirm before prep'],
    },
    opportunityWalkinSummary: [
      'Unconfirmed filling visit — call script used twice. Confirm before setting up operatory.',
      'If seated, allow buffer time; patient shuts down if rushed.',
    ],
    opportunityWalkoutSummary: [
      'Collect $35 copay if visit completed.',
      'Require text confirmation for next visit — card on file per policy.',
    ],
    family: [
      { name: 'Thomas Wiggins', rel: 'Brother', status: 'Also a patient' },
      { name: 'Claire', rel: 'Niece', status: 'Pedo patient' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Tea preferred' },
      { label: 'Music', value: 'Soft jazz' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$7,880' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '06 / 10' },
      { label: 'Balance', value: '$45', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Yesterday, 5:10 PM',
        text: 'Second confirmation call — no answer. Left voicemail with same-day callback number.',
      },
    ],
  },
  a6: {
    clinicalAlerts: {
      allergies: ['Latex'],
      conditions: ['None active'],
      medications: ['Multivitamin daily'],
      mindful: ['Prefers hygienist Daniel', 'Reliable attendee'],
    },
    opportunityWalkinSummary: [
      'Routine prophy — all ELVA checks passed. Standard preventive visit.',
      'Good candidate for same-day fluoride if insurance allows.',
    ],
    opportunityWalkoutSummary: [
      'Book 6-month recall before checkout.',
      'Mention electric toothbrush rebate program if asked.',
    ],
    family: [
      { name: 'David Reid', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Olivia', rel: 'Daughter', status: 'Ortho elsewhere' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Cappuccino' },
      { label: 'Music', value: 'Podcasts' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$5,430' },
      { label: 'Health score', value: '09 / 10' },
      { label: 'Relationship', value: '09 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Daniel (Hygienist)',
        when: 'Today, 9:30 AM',
        text: 'Patient checked in early. Forms and eligibility all green.',
      },
    ],
  },
  a7: {
    clinicalAlerts: {
      allergies: ['Latex', 'Sulfa'],
      conditions: ['Type 2 diabetes', 'Hypertension'],
      medications: ['Metformin 500mg', 'Losartan 50mg'],
      mindful: ['Verify temp crown comfort before dismissal', 'Prefers Olivia for restorative'],
    },
    opportunityWalkinSummary: [
      'Crown + buildup delivery today — confirm shade match and temp comfort before cement.',
      'High production visit ($1,830) — allow full block; review home care for new crown.',
    ],
    opportunityWalkoutSummary: [
      'Collect $415 estimated portion before dismissal — pre-auth approved Mar 28.',
      'Schedule 2-week bite check if sensitivity reported after anesthesia wears off.',
    ],
    family: [
      { name: 'Maria Johnson', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Carlo', rel: 'Son', status: 'Hygiene in 2 weeks' },
      { name: 'Giulia', rel: 'Daughter', status: 'Away at college' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Espresso' },
      { label: 'Music', value: 'Opera' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None needed' },
    ],
    patientValue: [
      { label: 'Total value', value: '$15,540' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '09 / 10' },
      { label: 'Balance', value: '$0', tone: 'green' },
    ],
    notes: [
      {
        author: 'Olivia (Clinical)',
        when: 'May 19, 2:15 PM',
        text: 'Lab case verified — shade A2 matches prep. Temp intact at try-in.',
      },
      {
        author: 'Front desk',
        when: 'Today, 12:45 PM',
        text: 'Patient arrived on time. Pre-auth and lab checklist complete.',
      },
    ],
  },
  a8: {
    clinicalAlerts: {
      allergies: ['Penicillin'],
      conditions: ['Hypertension'],
      medications: ['Lisinopril 10mg'],
      mindful: ['No-show pattern — confirmation required', 'Third miss in 12 months'],
    },
    opportunityWalkinSummary: [
      'No-show for crown prep — do not hold operatory. Run no-show protocol.',
      'Automated text sent 3:15pm. Front desk to call before end of day.',
    ],
    opportunityWalkoutSummary: [
      'Reschedule with 48h confirmation policy and card on file.',
      'Review broken appointment fee policy with office manager if applicable.',
    ],
    family: [
      { name: 'Angela Love', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Jordan', rel: 'Son', status: 'Sports guard ordered' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'Hip hop' },
      { label: 'Blanket', value: 'No' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$8,200' },
      { label: 'Health score', value: '06 / 10' },
      { label: 'Relationship', value: '05 / 10' },
      { label: 'Balance', value: '$150', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 3:15 PM',
        text: 'No-show logged. Automated message sent. Callback queued for 4:30pm.',
      },
    ],
  },
  a9: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Previous root canal #19'],
      medications: ['None daily'],
      mindful: ['Prefers afternoon slots only', 'Forms incomplete for consult'],
    },
    opportunityWalkinSummary: [
      'Endo consult rescheduled to May 28 — slot freed for same-day opportunity.',
      'Complete intake forms before consult date; referral letter on file.',
    ],
    opportunityWalkoutSummary: [
      'Re-verify Principal PPO benefits before May 28 visit.',
      'Send forms link again with consult date in subject line.',
    ],
    family: [
      { name: 'Carmen Manning', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Lucas', rel: 'Son', status: 'Pedo recall due' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Regular' },
      { label: 'Music', value: 'News radio' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$3,890' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '07 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 11:00 AM',
        text: 'Reschedule confirmed for May 28 at 2pm. Email confirmation sent.',
      },
    ],
  },
  a10: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Osteopenia'],
      medications: ['Vitamin D 2000 IU', 'Calcium supplement'],
      mindful: ['VIP referrer — thank personally', 'Implant integration stable per last visit'],
    },
    opportunityWalkinSummary: [
      'Implant follow-up — VIP sent 4 referrals YTD. Thank him before clinical handoff.',
      'Post-op check only; no production pressure. Ask if anyone else needs scheduling.',
    ],
    opportunityWalkoutSummary: [
      'Update implant stability notes in chart.',
      'Send referral thank-you note if new referral mentioned today.',
    ],
    family: [
      { name: 'Susan Greer', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Patrick', rel: 'Son', status: 'Referred 2 patients' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black, no sugar' },
      { label: 'Music', value: 'Classical' },
      { label: 'Blanket', value: 'No' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Referrals YTD', value: '4 patients', tone: 'green' },
      { label: 'Total value', value: '$48,200' },
      { label: 'Health score', value: '09 / 10', tone: 'green' },
      { label: 'Relationship', value: '10 / 10', tone: 'green' },
    ],
    notes: [
      {
        author: 'Dr. Carter',
        when: 'Feb 20, 10:30 AM',
        text: 'Implant integration excellent. Next recall in 6 months unless sensitivity.',
      },
    ],
  },
  a11: {
    clinicalAlerts: {
      allergies: ['Penicillin', 'Latex', 'Sulfa', 'Aspirin', 'Codeine'],
      conditions: ['Mild hypertension', 'Sleep apnea (CPAP)', 'Type 2 diabetes', 'Osteoporosis', 'GERD', 'Anxiety disorder'],
      medications: ['Metformin 1000mg', 'Lisinopril 20mg', 'Atorvastatin 40mg', 'Sertraline 50mg'],
      mindful: ['Sensitive to needles', 'Anxiety: not in control', 'Slow to respond to anesthesia'],
    },
    opportunityWalkinSummary: [
      'Mom of 3 with $1,200 unscheduled treatment across the family. Worth a warm onboarding — long-term household potential.',
      'Anxiety flag in chart. Offer nitrous before procedure; prep Op-3 (the quiet room). She shuts down if rushed.',
    ],
    opportunityWalkoutSummary: [
      'Mention husband + son are both 8 months overdue. Sara is the household decision-maker — book all 3 today if open.',
      'Discuss whitening estimate before she leaves. Use the wedding timeline as the framing.',
    ],
    family: [
      { name: 'Jane Brooks', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Emily', rel: 'Daughter', status: '8mo overdue', overdue: true },
      { name: 'Michael', rel: 'Son', status: 'Also a patient' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'Jazz' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'Nitrous (anxiety)' },
    ],
    patientValue: [
      { label: 'Total value', value: '$11,240' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$340', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Mary (Front desk)',
        when: 'Yesterday, 4:32 PM',
        text: 'Called to confirm — needs parking validation. Reserved spot 12.',
      },
      {
        author: 'Dr. Wilkinson',
        when: 'May 18, 11:08 AM',
        text: 'Husband Jane called — interested in consult. Pass to Sara when Mrs C is here. Do not push.',
      },
    ],
  },
  a12: {
    clinicalAlerts: {
      allergies: ['Penicillin'],
      conditions: ['Atrial fibrillation', 'On Coumadin', 'Osteoporosis'],
      medications: ['Warfarin 5mg', 'Metoprolol 25mg'],
      mindful: ['Bleeding risk — confirm INR', 'Medical alert needs physician clearance'],
    },
    opportunityWalkinSummary: [
      'Broken implant appointment — do not seat. Medical clearance and INR required first.',
      'Pre-auth pending with Aetna Medicare — call patient to rebook after med clear.',
    ],
    opportunityWalkoutSummary: [
      'Obtain physician clearance before any reschedule.',
      'Confirm INR within 48h of new surgical date.',
    ],
    family: [
      { name: 'Ruth Hayes', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Daniel', rel: 'Son', status: 'Emergency contact' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Decaf only' },
      { label: 'Music', value: 'Soft classical' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'Discuss with MD first' },
    ],
    patientValue: [
      { label: 'Total value', value: '$22,100' },
      { label: 'Health score', value: '06 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Clinical coordinator',
        when: 'May 11, 9:00 AM',
        text: 'Pre-auth submitted. Waiting on physician clearance letter for Coumadin management.',
      },
    ],
  },
  a13: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Moderate chronic periodontitis'],
      medications: ['None daily'],
      mindful: ['SRP quadrant 1 in progress', 'Good anesthesia response May 6'],
    },
    opportunityWalkinSummary: [
      'SRP quadrant 1 today — chlorhexidine rinse protocol. Allow 90 minutes.',
      'Schedule quadrant 2 within 14 days before inflammation returns.',
    ],
    opportunityWalkoutSummary: [
      'Collect $88 estimated copay at checkout.',
      'Confirm chlorhexidine pickup and post-op instructions signed.',
    ],
    family: [
      { name: 'Robert Pierce', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Nina', rel: 'Daughter', status: 'Away at school' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Green tea' },
      { label: 'Music', value: 'Ambient' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'Topical only' },
    ],
    patientValue: [
      { label: 'Total value', value: '$8,760' },
      { label: 'Health score', value: '06 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Dr. Carter',
        when: 'May 6, 2:00 PM',
        text: 'Quadrant 1 completed with good hemostasis. Quadrant 2 scheduled in 2 weeks.',
      },
    ],
  },
  a14: {
    clinicalAlerts: {
      allergies: ['Latex'],
      conditions: ['Severe wear', 'TMJ evaluation pending'],
      medications: ['Meloxicam 7.5mg PRN'],
      mindful: ['Big case — TC coordinator greets first', 'Allow 90 min consult block'],
    },
    opportunityWalkinSummary: [
      'Full mouth rehab consult — allow 90 minutes. TC coordinator should greet before provider.',
      'Pre-auth pending on phase 1 ($4,200) — mention timeline transparently if asked.',
    ],
    opportunityWalkoutSummary: [
      'Present phased treatment plan with CBCT findings.',
      'Schedule TC follow-up call within 48 hours.',
    ],
    family: [
      { name: 'Margaret Gibbons', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Andrew', rel: 'Son', status: 'Referral source' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'None' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'Discuss for long visits' },
    ],
    patientValue: [
      { label: 'Total value', value: '$4,200 est.' },
      { label: 'Health score', value: '05 / 10' },
      { label: 'Relationship', value: '07 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'TC Coordinator',
        when: 'May 19, 8:00 AM',
        text: 'CBCT uploaded yesterday. Phased plan draft ready for provider review.',
      },
    ],
  },
  a15: {
    clinicalAlerts: {
      allergies: ['Penicillin', 'Latex'],
      conditions: ['Mild hypertension', 'Anxiety disorder'],
      medications: ['Lisinopril 20mg', 'Sertraline 50mg'],
      mindful: ['Same patient as 12pm visit — nitrous if anxiety spikes', 'Nitrous before any procedure'],
    },
    opportunityWalkinSummary: [
      'Second visit today — crown seat after new patient exam this morning.',
      'Shade already selected at prep. Confirm occlusion and contacts before cement.',
    ],
    opportunityWalkoutSummary: [
      'Collect $285 crown seat portion — benefits verified at earlier visit.',
      'Schedule sensitivity follow-up if needed within 48 hours.',
    ],
    family: [
      { name: 'Jane Brooks', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Emily', rel: 'Daughter', status: '8mo overdue', overdue: true },
      { name: 'Michael', rel: 'Son', status: 'Also a patient' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'Jazz' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'Nitrous (anxiety)' },
    ],
    patientValue: [
      { label: 'Total value', value: '$11,240' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$340', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Marcus (Clinical)',
        when: 'Today, 12:30 PM',
        text: 'Morning exam complete. Crown seat at 1pm — shade A2, nitrous on standby.',
      },
    ],
  },
  a16: {
    clinicalAlerts: {
      allergies: ['Codeine'],
      conditions: ['Hypertension controlled'],
      medications: ['Amlodipine 5mg'],
      mindful: ['Verify lab shade A2 before cement', 'Financial consent still needed'],
    },
    opportunityWalkinSummary: [
      'Bridge seat — lab case in-house since May 19. Try-in approved May 12.',
      'Obtain signed financial consent before seating if not on file.',
    ],
    opportunityWalkoutSummary: [
      'Collect $220 bridge seat portion at checkout.',
      'Post-op care instructions and floss threader demo at dismissal.',
    ],
    family: [
      { name: 'Thomas Wiggins', rel: 'Brother', status: 'Also a patient' },
      { name: 'Claire', rel: 'Niece', status: 'Pedo patient' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Tea preferred' },
      { label: 'Music', value: 'Soft jazz' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$7,880' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '07 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'May 19, 9:00 AM',
        text: 'Lab case confirmed in office. Financial consent form sent to tablet.',
      },
    ],
  },
  a17: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['None active'],
      medications: ['Birth control daily'],
      mindful: ['Lab delay — patient aware', 'Do not attempt seat without case'],
    },
    opportunityWalkinSummary: [
      'Rescheduled — crown lab case #8842 not arrived. ETA May 22.',
      'Patient notified by text. No charge for today.',
    ],
    opportunityWalkoutSummary: [
      'Track lab ticket #8842 daily until received.',
      'Rebook within 5 days of lab receipt — patient prefers late afternoon.',
    ],
    family: [
      { name: 'Marcus Dorsey', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Tyler', rel: 'Son', status: 'Sports guard due' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Latte' },
      { label: 'Music', value: 'Pop' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$6,540' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 4:45 PM',
        text: 'Lab delay text sent. Reschedule link offered for May 24 or later.',
      },
    ],
  },
  a18: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Hearing impairment'],
      medications: ['Aspirin 81mg'],
      mindful: ['Speak facing patient clearly', 'Hard of hearing — no mumbled handoffs'],
    },
    opportunityWalkinSummary: [
      'Long-time patient — first slot preference. Completed early last visit.',
      'Routine prophy under Medicare supplement — fully covered.',
    ],
    opportunityWalkoutSummary: [
      'Book 6-month recall before checkout.',
      'Update medical history if any new medications since January.',
    ],
    family: [
      { name: 'Helen Paul', rel: 'Spouse', status: 'Also a patient' },
      { name: 'George', rel: 'Son', status: 'Power of attorney on file' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Decaf' },
      { label: 'Music', value: 'Off preferred' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$12,400' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '10 / 10', tone: 'green' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Daniel (Hygienist)',
        when: 'Nov 20, 8:15 AM',
        text: 'Completed prophy 15 min early. Stable perio — continue 6-month recall.',
      },
    ],
  },
  a19: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Chronic periodontitis maintenance'],
      medications: ['None daily'],
      mindful: ['Unconfirmed today — call before seating', '$89 balance on account'],
    },
    opportunityWalkinSummary: [
      'Perio maintenance with balance flag — confirm appointment before setup.',
      'Review $89 August balance gently at checkout, not in chair.',
    ],
    opportunityWalkoutSummary: [
      'Collect $89 balance + estimated $40 copay if visit proceeds.',
      'Book next perio maintenance interval before leaving.',
    ],
    family: [
      { name: 'Linda Ferrell', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Chris', rel: 'Son', status: 'Inactive 2 years' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'Country' },
      { label: 'Blanket', value: 'No' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$5,980' },
      { label: 'Health score', value: '06 / 10' },
      { label: 'Relationship', value: '06 / 10' },
      { label: 'Balance', value: '$89', tone: 'warning' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 8:30 AM',
        text: 'Unconfirmed perio visit — left voicemail. Balance note added to checkout script.',
      },
    ],
  },
  a20: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['None active'],
      medications: ['None daily'],
      mindful: ['In treatment since 1:04pm', 'No issues reported mid-visit'],
    },
    opportunityWalkinSummary: [
      'Prophy in progress — standard preventive, fully covered.',
      'Complete post-op instructions at checkout when dismissed.',
    ],
    opportunityWalkoutSummary: [
      'Confirm 6-month recall scheduled before patient leaves.',
      'Offer fluoride if insurance frequency allows.',
    ],
    family: [
      { name: 'Sarah Baird', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Ethan', rel: 'Son', status: 'Ortho active' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Regular' },
      { label: 'Music', value: 'Classic rock' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$4,320' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '08 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Daniel (Hygienist)',
        when: 'Today, 1:04 PM',
        text: 'Patient seated on time. Prophy started — no sensitivity reported.',
      },
    ],
  },
  a21: {
    clinicalAlerts: {
      allergies: ['None documented'],
      conditions: ['Stable perio'],
      medications: ['Statin daily'],
      mindful: ['Birthday today — handshake preferred over singing', 'Recall every 6 months'],
    },
    opportunityWalkinSummary: [
      'Birthday prophy — card at front desk. Stable perio, routine visit.',
      'Happy birthday greeting with handshake if patient comfortable.',
    ],
    opportunityWalkoutSummary: [
      'Offer signed birthday card at checkout.',
      'Book next 6-month recall before dismissal.',
    ],
    family: [
      { name: 'Patricia Brooks', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Sam', rel: 'Grandson', status: 'Pedo patient' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Black' },
      { label: 'Music', value: 'Big band' },
      { label: 'Blanket', value: 'Yes' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$9,100' },
      { label: 'Health score', value: '08 / 10' },
      { label: 'Relationship', value: '09 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 8:00 AM',
        text: 'Birthday flagged. Card signed by team — handshake note in chart.',
      },
    ],
  },
  a22: {
    clinicalAlerts: {
      allergies: ['Codeine'],
      conditions: ['None active'],
      medications: ['None daily'],
      mindful: ['Interested in clear aligners — Invis-Line engaged'],
    },
    opportunityWalkinSummary: [
      'Orthodontic benefit unused — aligner consult is the natural next step.',
      'Today\'s filling is covered; patient share is small.',
    ],
    opportunityWalkoutSummary: [
      'Point to the Aligners card for what to say about Invisalign.',
      'Book aligner consult if patient says yes on the Invis-Line.',
    ],
    family: [
      { name: 'Dorothy Allison', rel: 'Spouse', status: 'Also a patient' },
      { name: 'Paul', rel: 'Son', status: 'Handles insurance calls' },
    ],
    comfort: [
      { label: 'Coffee', value: 'Espresso' },
      { label: 'Music', value: 'Jazz' },
      { label: 'Blanket', value: 'Optional' },
      { label: 'Sedation', value: 'None' },
    ],
    patientValue: [
      { label: 'Total value', value: '$5,670' },
      { label: 'Health score', value: '07 / 10' },
      { label: 'Relationship', value: '07 / 10' },
      { label: 'Balance', value: '$0' },
    ],
    notes: [
      {
        author: 'Front desk',
        when: 'Today, 7:48 AM',
        text: 'Eligibility check failed. Requested photo of new insurance card — not received yet.',
      },
    ],
  },
};
